import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

import { publicApi } from '../utils/api';
import { FEATURED_GUIDES } from '../data/editorialContent';
import { IMG } from '../lib/images';
import { useTheme } from '../contexts/ThemeContext';

const mergeGuidesBySlug = (guides = []) => {
  const bySlug = new Map();
  [...FEATURED_GUIDES, ...guides].forEach((guide) => {
    if (!guide?.slug) return;
    bySlug.set(guide.slug, guide);
  });
  return Array.from(bySlug.values());
};

const GuidesPage = () => {
  const { theme } = useTheme();
  const isLight = theme === 'light';
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await publicApi.getGuides();
        setGuides(mergeGuidesBySlug(response.data?.items || []));
      } catch {
        setGuides(mergeGuidesBySlug());
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  return (
    <div className="min-h-screen pt-24 page-bg-shell page-bg-devices" data-testid="guides-page">
      <section
        className="relative overflow-hidden resource-hero"
        style={{
          backgroundImage: isLight
            ? `linear-gradient(rgba(255,249,236,0.68), rgba(247,252,255,0.82)), url(${IMG.hero.teamCollaborate})`
            : `linear-gradient(rgba(8,8,18,0.72), rgba(8,8,18,0.94)), url(${IMG.hero.teamCollaborate})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="absolute inset-0 prime-aurora-bg" />
        <div className="container mx-auto px-6 py-16 relative z-10">
      <div className="max-w-3xl mb-10">
        <p className="text-xs uppercase font-mono mb-3" style={{ color: 'var(--text-muted)' }}>Guides</p>
        <h1 className="text-4xl font-black mb-4" style={{ fontFamily: 'Unbounded, sans-serif', color: 'var(--text-primary)' }}>
          Buyer guides and rollout playbooks for practical AI adoption.
        </h1>
        <p className="text-base" style={{ color: 'var(--text-secondary)' }}>
          Field-ready guides with concrete rollout steps, visual flow previews, and implementation notes for small teams.
        </p>
      </div>
        </div>
      </section>

      <section className="container mx-auto px-6 pb-16">

      {loading ? (
        <div className="terminal-grid p-6"><p style={{ color: 'var(--text-secondary)' }}>Loading guides...</p></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {guides.map((guide) => (
            <article key={guide.id || guide.slug} className="terminal-grid overflow-hidden">
              {guide.cover_image && (
                <img src={guide.cover_image} alt={guide.title} className="w-full h-44 object-cover" />
              )}
              <div className="p-6">
              <p className="text-xs uppercase font-mono mb-2" style={{ color: 'var(--text-muted)' }}>{guide.category || 'guide'}</p>
              <h2 className="text-xl font-bold text-white mb-3" style={{ fontFamily: 'Unbounded, sans-serif' }}>{guide.title}</h2>
              <p className="text-sm mb-4" style={{ color: 'var(--text-secondary)' }}>{guide.excerpt || 'No excerpt provided yet.'}</p>
              {guide.visual?.steps?.length > 0 && (
                <div className="rounded-xl p-4 mb-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--border-subtle)' }}>
                  <p className="text-xs uppercase font-mono mb-3" style={{ color: 'var(--text-muted)' }}>{guide.visual.title || 'Flow preview'}</p>
                  <div className="space-y-3">
                    {guide.visual.steps.slice(0, 3).map((step, index) => (
                      <div key={step.label} className="flex items-start gap-3">
                        <div className="w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold" style={{ background: 'rgba(255,232,0,0.12)', color: 'var(--accent-primary)' }}>
                          {index + 1}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{step.label}</p>
                          <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>{step.detail}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <Link to={`/guides/${guide.slug}`} className="btn-secondary inline-flex items-center gap-2 w-fit">
                Read guide <ArrowRight size={14} />
              </Link>
              </div>
            </article>
          ))}
        </div>
      )}
      </section>
    </div>
  );
};

export default GuidesPage;
